/**
 * Budget Overview — period controls, summary KPIs, overall progress, plan cards
 */

import React, { useState } from 'react';
import {
  Wallet,
  ShoppingCart,
  CircleDollarSign,
  Loader2,
  Plus,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useBudgetOverview, useBudgetPlans } from '../../hooks/useBudgetOverview';
import { useRegion } from '../../contexts/RegionContext';
import type { Budget, BudgetPeriodType } from '../../types/budget.types';
import { getPeriodBounds, shiftPeriod, todayIsoDate } from '../../utils/budget-period';
import { BudgetPlanCard } from './BudgetPlanCard';

const PERIODS: { value: BudgetPeriodType; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

function getProgressBarColor(percentUsed: number): string {
  if (percentUsed >= 100) return 'bg-red-500';
  if (percentUsed >= 90) return 'bg-orange-500';
  if (percentUsed >= 75) return 'bg-yellow-500';
  return 'bg-green-500';
}

interface BudgetOverviewProps {
  onCreateBudget?: () => void;
  onViewDetails?: (budget: Budget) => void;
  onEdit?: (budget: Budget) => void;
}

export function BudgetOverview({
  onCreateBudget,
  onViewDetails,
  onEdit,
}: BudgetOverviewProps): React.ReactElement {
  const [periodType, setPeriodType] = useState<BudgetPeriodType>('monthly');
  const [periodAnchor, setPeriodAnchor] = useState<string>(() => todayIsoDate());
  const { data: overview, isLoading: overviewLoading } = useBudgetOverview(periodType, periodAnchor);
  const { data: plans = [], isLoading: plansLoading } = useBudgetPlans(periodType, periodAnchor);
  const { formatCurrency } = useRegion();

  const handlePeriodTypeChange = (next: BudgetPeriodType) => {
    setPeriodType(next);
    setPeriodAnchor(todayIsoDate());
  };

  const isLoading = overviewLoading || plansLoading;

  const periodLabel =
    overview?.periodLabel ??
    plans[0]?.periodLabel ??
    getPeriodBounds(periodType, periodAnchor).label;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" aria-label="Loading budget overview" />
      </div>
    );
  }

  const totalBudget = overview?.totalBudget ?? 0;
  const totalSpent = overview?.totalSpent ?? 0;
  const totalRemaining = overview?.totalRemaining ?? 0;
  const percentUsed = overview?.percentUsed ?? 0;
  const percentRemaining = overview?.percentRemaining ?? (totalBudget > 0 ? 100 - percentUsed : 100);
  const hasPlans = plans.length > 0;
  const hasBudget = totalBudget > 0;

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 shrink-0">
            Budget Overview
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex space-x-2" role="group" aria-label="Budget period type">
              {PERIODS.map((period) => (
                <button
                  key={period.value}
                  type="button"
                  onClick={() => handlePeriodTypeChange(period.value)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    periodType === period.value
                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                  aria-pressed={periodType === period.value}
                >
                  {period.label}
                </button>
              ))}
            </div>
            {onCreateBudget && (
              <button
                type="button"
                onClick={onCreateBudget}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium shadow-sm shrink-0"
              >
                <Plus className="h-4 w-4" />
                Create Budget
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mb-6">
          <button
            type="button"
            onClick={() => setPeriodAnchor((a) => shiftPeriod(periodType, a, -1))}
            className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Previous period"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="min-w-[10rem] text-center text-sm font-medium text-gray-900 dark:text-gray-100">
            {periodLabel}
          </span>
          <button
            type="button"
            onClick={() => setPeriodAnchor((a) => shiftPeriod(periodType, a, 1))}
            className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Next period"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {!hasPlans && !hasBudget ? (
          <div className="text-center py-6">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              No active budget plans. Create a plan to start tracking your spending.
            </p>
            {onCreateBudget && (
              <button
                type="button"
                onClick={onCreateBudget}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                Create Budget
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="main-page-grid-3-up mb-6">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Budget</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(totalBudget)}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/40 rounded-lg flex items-center justify-center">
                    <Wallet className="h-6 w-6 text-purple-600 dark:text-purple-400" aria-hidden />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-lg p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Spent</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(totalSpent)}
                    </p>
                    <p className="text-sm text-orange-600 dark:text-orange-400">{percentUsed}% of budget</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/40 rounded-lg flex items-center justify-center">
                    <ShoppingCart className="h-6 w-6 text-orange-600 dark:text-orange-400" aria-hidden />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Remaining</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(totalRemaining)}
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-400">{percentRemaining}% left</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/40 rounded-lg flex items-center justify-center">
                    <CircleDollarSign className="h-6 w-6 text-green-600 dark:text-green-400" aria-hidden />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">Overall Progress</h3>
                <span className="text-sm text-gray-600 dark:text-gray-400">{percentUsed}% used</span>
              </div>
              <div
                className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3"
                role="progressbar"
                aria-valuenow={percentUsed}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Overall budget progress"
              >
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${getProgressBarColor(percentUsed)}`}
                  style={{ width: `${Math.min(100, percentUsed)}%` }}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {hasPlans && (
        <div className="main-page-grid-2">
          {plans.map((plan) => (
            <BudgetPlanCard
              key={plan.id}
              budget={plan}
              onViewDetails={onViewDetails ?? (() => undefined)}
              onEdit={onEdit ?? (() => undefined)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
